import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { ArrowSquareOut, PencilSimple } from "@phosphor-icons/react"
import { ListItem, ListText } from "@/components/ui/list"
import { Tag } from "@/components/ui/tag"
import { cn } from "@/lib/utils"
import { ChMeasRadiologyPrescriptionDrawer } from "@/modules/patients/detail/meas-radiology-prescription/ch/ch-meas-radiology-prescription-drawer"
import { REQUEST_EXAMINATION_GROUP } from "@/modules/patients/detail/meas-radiology-prescription/ch/constants"
import { chMeasRadiologyPrescriptionFragment$key } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/__generated__/chMeasRadiologyPrescriptionFragment.graphql"
import { chMeasRadiologyPrescriptionFragment } from "@/modules/patients/detail/meas-radiology-prescription/ch/services/chMeasRadiologyPrescriptionFragment"
import { mapValuesToOptions } from "@/modules/patients/detail/meas-radiology-prescription/utils/map-values-to-options"
import { DocumentProps } from "@/modules/patients/detail/progress-note/types/document"
import { useConcludeDialogStoreContext } from "@/modules/patients/detail/store/useConcludeDialogStore"
import { chProgressNoteFragment$data } from "@/modules/patients/services/ch/__generated__/chProgressNoteFragment.graphql"
import { useDrawerStore } from "@/stores/useDrawerStore"

interface MeasRadiologyPrescriptionDocumentProps extends DocumentProps {
  id?: string
  progressNoteId: string
  measRadiologyPrescriptionRef: chMeasRadiologyPrescriptionFragment$key
  isEditable?: boolean
  fileUploadRequest?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["fileUploadRequest"]
  medgateConnectOrder?: NonNullable<chProgressNoteFragment$data["followUpAppointment"]>["medgateConnectOrder"]
}

export const RadiologyIcon = ({ isSelected }: { isSelected?: boolean }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      className="h-6 w-6 shrink-0">
      <path
        d="M5.3077 21.4981C4.80257 21.4981 4.375 21.3231 4.025 20.9731C3.675 20.6231 3.5 20.1956 3.5 19.6904V6.30589C3.5 5.80076 3.675 5.37319 4.025 5.02319C4.375 4.67319 4.80257 4.49819 5.3077 4.49819H6.69233V2.38281H8.23075V4.49819H15.8077V2.38281H17.3076V4.49819H18.6923C19.1974 4.49819 19.625 4.67319 19.975 5.02319C20.325 5.37319 20.5 5.80076 20.5 6.30589V19.6904C20.5 20.1956 20.325 20.6231 19.975 20.9731C19.625 21.3231 19.1974 21.4981 18.6923 21.4981H5.3077ZM4.99997 19.6904C4.99997 19.7674 5.03202 19.8379 5.09612 19.902C5.16024 19.9661 5.23077 19.9982 5.3077 19.9982H18.6923C18.7692 19.9982 18.8397 19.9661 18.9038 19.902C18.9679 19.8379 19 19.7674 19 19.6904V6.30589C19 6.22896 18.9679 6.15843 18.9038 6.09431C18.8397 6.03021 18.7692 5.99816 18.6923 5.99816H5.3077C5.23077 5.99816 5.16024 6.03021 5.09612 6.09431C5.03202 6.15843 4.99997 6.22896 4.99997 6.30589V19.6904ZM9.3077 19.9982V17.9962L7.43075 15.7904C7.26665 15.6199 7.12178 15.3956 6.99615 15.1174C6.87052 14.8392 6.8077 14.5174 6.8077 14.152C6.8077 13.9482 6.82853 13.7501 6.8702 13.5578C6.91187 13.3655 6.97949 13.1789 7.07308 12.9982C6.98974 12.8276 6.92467 12.6462 6.87788 12.4539C6.83109 12.2616 6.8077 12.0584 6.8077 11.8443C6.8077 11.4789 6.87052 11.1572 6.99615 10.8789C7.12178 10.6007 7.26665 10.3764 7.43075 10.2059L9.3077 8.00011V5.99816H10.5V8.16931C10.5 8.25265 10.4416 8.41098 10.325 8.64431L8.32498 10.9943C8.20831 11.1276 8.12498 11.2651 8.07498 11.4068C8.02497 11.5485 7.99998 11.6943 7.99998 11.8443C7.99998 12.1776 8.10831 12.4651 8.32498 12.7068C8.54164 12.9485 8.81664 13.0693 9.14998 13.0693C9.29998 13.0693 9.44004 13.0443 9.57018 12.9943C9.70029 12.9443 9.81856 12.861 9.92498 12.7443C10.2083 12.461 10.5291 12.2443 10.8875 12.0943C11.2458 11.9443 11.6166 11.8693 12 11.8693C12.3833 11.8693 12.7541 11.9443 13.1125 12.0943C13.4708 12.2443 13.7916 12.461 14.075 12.7443C14.1916 12.861 14.3166 12.9443 14.45 12.9943C14.5833 13.0443 14.7166 13.0693 14.85 13.0693C15.1833 13.0693 15.4583 12.9485 15.675 12.7068C15.8916 12.4651 16 12.186 16 11.8693C16 11.7193 15.9708 11.5735 15.9125 11.4318C15.8541 11.2901 15.775 11.1526 15.675 11.0193L13.675 8.64431C13.6083 8.57765 13.5625 8.50265 13.5375 8.41931C13.5125 8.33598 13.5 8.25265 13.5 8.16931V5.99816H14.6923V8.00971L16.5846 10.2463C16.7923 10.4809 16.9455 10.7363 17.0442 11.0126C17.1429 11.2889 17.1922 11.5744 17.1922 11.8693C17.1922 12.0732 17.1663 12.2712 17.1144 12.4636C17.0625 12.6559 16.9948 12.8341 16.9115 12.9982C17.0051 13.1789 17.0753 13.3655 17.1221 13.5578C17.1689 13.7501 17.1922 13.9482 17.1922 14.152C17.1922 14.5174 17.1294 14.8392 17.0038 15.1174C16.8782 15.3956 16.7333 15.6199 16.5692 15.7904L14.6923 17.9962V19.9982H13.5V17.827C13.5 17.727 13.5583 17.5687 13.675 17.352L15.675 15.002C15.8045 14.8494 15.8942 14.6837 15.9442 14.5049C15.9942 14.326 16.0032 14.1488 15.9711 13.9731C15.8006 14.0693 15.6253 14.1414 15.4452 14.1895C15.265 14.2376 15.075 14.2616 14.875 14.2616C14.5801 14.2616 14.2868 14.203 13.9952 14.0856C13.7035 13.9683 13.4455 13.7924 13.2212 13.5578C13.0724 13.3924 12.8913 13.2683 12.6779 13.1856C12.4644 13.103 12.2384 13.0616 12 13.0616C11.7782 13.0616 11.5589 13.103 11.3423 13.1856C11.1256 13.2683 10.9378 13.3924 10.7788 13.5578C10.5609 13.7924 10.307 13.9683 10.0173 14.0856C9.72752 14.203 9.43522 14.2616 9.14035 14.2616C8.94035 14.2616 8.74773 14.2376 8.56248 14.1895C8.37723 14.1414 8.19934 14.0693 8.02882 13.9731C7.99677 14.1488 8.00575 14.326 8.05575 14.5049C8.10575 14.6837 8.19549 14.8494 8.32498 15.002L10.325 17.352C10.375 17.4353 10.4166 17.5145 10.45 17.5895C10.4833 17.6645 10.5 17.7437 10.5 17.827V19.9982H9.3077ZM4.99997 19.6904V19.9982V5.99816V19.6904Z"
        className={isSelected ? "fill-on-primary-surface" : "fill-on-surface-subdued"}
      />
    </svg>
  )
}

const MeasRadiologyPrescriptionDocument = ({
  id,
  progressNoteId,
  measRadiologyPrescriptionRef,
  isEditable = true,
  isConclusion,
  isSigned,
  isSent,
  fileUploadRequest,
  medgateConnectOrder,
  onSelect,
}: MeasRadiologyPrescriptionDocumentProps) => {
  const { t } = useTranslation()
  const { requestExaminations } = useFragment(chMeasRadiologyPrescriptionFragment, measRadiologyPrescriptionRef) ?? {}
  const selectedDocumentId = useConcludeDialogStoreContext((state) => state.selectedDocument?.id)
  const openDrawer = useDrawerStore((state) => state.openDrawer)
  const isSelected = isConclusion && selectedDocumentId === id
  const triggeredValidation = useConcludeDialogStoreContext((state) => state.triggeredValidation)

  const allOptions = REQUEST_EXAMINATION_GROUP.flatMap((group) => group.options)
  const options = mapValuesToOptions(requestExaminations, allOptions)
  const selectedExaminationsString = options.map((option) => option.name).join(", ")

  return (
    <ListItem
      className={cn(
        "group hover:bg-surface-hovered flex h-12 items-center hover:cursor-pointer",
        isSelected && "bg-primary-surface hover:bg-primary-surface-hovered active:bg-primary-surface-pressed",
        !isEditable && !isConclusion && !isSent && "cursor-auto!",
      )}
      data-testid="meas-radiology-prescription-document-item"
      onClick={() => {
        if (!isEditable && id) {
          onSelect(id)
          return
        }
        openDrawer(
          () => (
            <ChMeasRadiologyPrescriptionDrawer
              id={id}
              progressNoteId={progressNoteId}
              radiologyPrescriptionRef={measRadiologyPrescriptionRef}
              fileUploadRequest={fileUploadRequest}
              medgateConnectOrder={medgateConnectOrder}
            />
          ),
          "MeasRadiologyPrescriptionDrawer",
        )
      }}>
      <RadiologyIcon isSelected={isSelected} />
      <div className={cn("ml-2 min-w-0", isEditable && "group-hover:mr-4")}>
        <ListText
          data-testid="summary-radiology-prescription-header"
          className={isSelected ? "text-on-primary-surface" : ""}>
          {t("patient.measRadiologyPrescription.radiologyPrescriptionMEAS")}
        </ListText>
        <ListText
          data-testid="meas-radiology-prescription-details"
          variant="secondary"
          className={cn("truncate", isSelected && "text-on-primary-surface")}>
          {selectedExaminationsString}
        </ListText>
      </div>
      {isEditable && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <PencilSimple className="text-on-surface-subdued" data-testid="meas-radiology-prescription-edit-button" />
          </div>
        </div>
      )}
      {!isConclusion && isSent && (
        <div className="ml-auto hidden group-hover:block">
          <div className="hover:bg-surface-hovered p-2 hover:rounded-full hover:p-2">
            <ArrowSquareOut
              className="text-on-surface-subdued"
              data-testid="meas-radiology-prescription-view-document-button"
            />
          </div>
        </div>
      )}
      {isConclusion && (triggeredValidation || isSigned) && (
        <Tag
          className="ml-auto group-hover:hidden"
          type={isSigned ? "success" : "critical"}
          label={isSigned ? t("patient.conclude.signed") : t("patient.conclude.signThis")}
        />
      )}
    </ListItem>
  )
}

export { MeasRadiologyPrescriptionDocument }
